import type { SizeVariant } from "~~/shared/types/store";

const normalizeSize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/**
 * Rozwiązuje etykietę rozmiaru na konkretny produkt-rodzeństwo (`sizeVariants`,
 * tryb allegrohash — każdy rozmiar to osobny produkt PrestaShop).
 *
 * Ten sam `rozmiar` może wystąpić w KILKU produktach (duplikaty) — np. jeden
 * wyprzedzony, drugi dostępny. Selektor renderuje klikalny przycisk wyłącznie
 * dla wariantu `available`, więc rozwiązywanie etykiety MUSI preferować wariant
 * dostępny (a wśród dostępnych — bieżący). Naiwne `find(v => v.rozmiar === x)`
 * bierze pierwszy po etykiecie i potrafi trafić na wyprzedzony duplikat, przez
 * co po zmianie rozmiaru `product.quantity` spada do 0, `isSoldOut` = true i
 * przycisk „Dodaj do koszyka" znika.
 *
 * Uwaga: na PDP `isCurrent` bywa patchowany na `true` dla wszystkich wariantów
 * o wybranej etykiecie, dlatego pierwszeństwo ma `available` (nie `isCurrent`).
 */
/**
 * Odtwarza flagę `isCurrent`, gdy backend nie oznaczył ŻADNEGO wariantu serii.
 *
 * `contelizerrest` (`ProductDetail::getSizeVariants()`) wystawia `is_current`
 * porównując `id_product` z `$currentId` — po refaktorze 4aa46df2 (2026-07-27) ta
 * zmienna przestała być przypisywana, więc PHP podstawiał `null` i flaga wracała
 * ZAWSZE jako `false`. Skutek na PDP: żaden rozmiar nie był podświetlony, etykieta
 * „Rozmiar: <wartość>" była pusta, a `resolveAddToCartTarget()` nie miał czego
 * dopasować. Front zna id bieżącego produktu, więc nie musi w tej sprawie ufać
 * backendowi — w trybie allegrohash każdy rozmiar to osobny produkt, więc wariant
 * o `id` równym bieżącemu produktowi JEST wariantem bieżącym.
 *
 * Gdy backend oznaczył wariant, zwracamy wejście bez zmian (nie przenosimy flagi —
 * PDP patchuje `isCurrent` także po wyborze użytkownika i to musi mieć pierwszeństwo).
 */
export function withCurrentVariantMarked(
  variants: readonly SizeVariant[] | undefined | null,
  currentProductId: string,
): SizeVariant[] {
  if (!variants?.length) return [];
  if (variants.some((v) => v.isCurrent)) return [...variants];

  const currentId = String(currentProductId ?? "").trim();
  if (!currentId) return [...variants];

  return variants.map((v) =>
    String(v.id).trim() === currentId ? { ...v, isCurrent: true } : v,
  );
}

export function resolveSizeVariant(
  variants: readonly SizeVariant[] | undefined | null,
  rozmiar: string,
): SizeVariant | undefined {
  if (!variants?.length) return undefined;
  const target = normalizeSize(rozmiar);
  const matches = variants.filter((v) => normalizeSize(v.rozmiar) === target);
  return (
    matches.find((v) => v.available && v.isCurrent) ??
    matches.find((v) => v.available) ??
    matches.find((v) => v.isCurrent) ??
    matches[0]
  );
}
